import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import { red } from '@material-ui/core/colors';
import Link from '@material-ui/core/Link';
import Chip from '@material-ui/core/Chip';
import DescriptionIcon from '@material-ui/icons/Description';
import DeleteIcon from '@material-ui/icons/Delete';
import { CircularProgress } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { KeyboardDatePicker } from "@material-ui/pickers";
import RoomIcon from '@material-ui/icons/Room';
import CardActions from '@material-ui/core/CardActions';

import { format } from 'date-fns';

import JSZip from 'jszip';
import JSZipUtils from 'jszip-utils';
import saveAs from 'save-as';

import { useDeleteNoteMutation } from '@graniot/common/adapters/note';
import { useAuth } from '@graniot/common/context/AuthContext';
import NoteMap from './NoteMap';
import GeomNote from './GeomNote';


const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    card: {
        marginBottom: theme.spacing(1),
    },
    attachment: {
        marginTop: theme.spacing(2),
        marginRight: theme.spacing(0.5),
    },
    avatar: {
        backgroundColor: red[500],
    },
    mapNoteContainer: {
        minHeight: 400
    },
}));



export default function Note({ note, parcel, selectedResolution, refetch }) {
    const classes = useStyles();
    const { t } = useTranslation();
    const { loggedUser, user } = useAuth();
    const useDelete = useDeleteNoteMutation();
    const [deleted, setDeleted] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [showMap, setShowMap] = React.useState(false);

    const isMenuOpen = Boolean(anchorEl);

    React.useEffect(() => {
        if (!useDelete.isLoading && useDelete.isSuccess) {
            useDelete.reset()
            setDeleted(true)
            refetch()
        }
    }, [useDelete])

    const handleDelete = () => {
        useDelete.mutate({ note, user })
    }

    const handleDownloadFiels = () => {
        const zip = new JSZip();
        let count = 0;
        const zipFilename = `${note.user?.username}_${format(new Date(note.created), 'hh:mm, EEE d, LLL yyyy')}.zip`;
        note.attachments.forEach(async function (url) {
            const filename = url.attachment.split("/").slice(-1)[0].split("?")[0];
            try {
                const file = await JSZipUtils.getBinaryContent(url.attachment)
                zip.file(filename, file, { binary: true });
                count++;
                if (count === note.attachments.length) {
                    zip.generateAsync({ type: 'blob' }).then(function (content) {
                        saveAs(content, zipFilename);
                    });
                }
            } catch (err) {
                console.log(err);
            }
        });
    }

    const handleNoteMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleShowMap = () => {
        // When hiding the map, allow time for control cleanup first
        if (showMap) {
            // Add a small delay to ensure proper cleanup
            setTimeout(() => {
                setShowMap(false);
            }, 100);
        } else {
            setShowMap(true);
        }
    }

    const menuId = 'primary-search-account-menu';
    const renderMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            id={menuId}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMenuOpen}
            onClose={handleMenuClose}
        >
            <MenuItem
                onClick={handleDownloadFiels}>
                <ListItemIcon>
                    <CloudDownloadIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit">{t('Note.Download')}</Typography>
            </MenuItem>
            {(loggedUser.username === note.user.username) && <MenuItem
                onClick={handleDelete}>
                <ListItemIcon >
                    {!useDelete.isLoading ? <DeleteIcon fontSize="small" color='secondary' /> : <CircularProgress color="secondary" size={20} />}
                </ListItemIcon>
                <Typography variant="inherit">{t('Note.Delete')}</Typography>
            </MenuItem>}
        </Menu>
    );

    return (
        <>
            {!deleted && <Card className={classes.root} variant="outlined">
                <CardHeader
                    avatar={
                        <Avatar aria-label="recipe" className={classes.avatar}>
                            {note.user?.username[0]}
                        </Avatar>
                    }
                    action={<IconButton
                        edge="end"
                        aria-label="account of current user"
                        aria-controls={menuId}
                        aria-haspopup="true"
                        onClick={handleNoteMenuOpen}
                        color="inherit"
                    >
                        <MoreVertIcon />
                    </IconButton>}
                    title={note.user?.username}
                    subheader={format(new Date(note.created), 'hh:mm, EEE d, LLL yyyy')}
                />
                <CardContent>

                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={12} md={8}>
                            <TextField
                                id="filled-multiline-flexible"
                                label={t(`Note.Note`)}
                                multiline
                                maxRows={4}
                                disabled
                                fullWidth
                                value={note.note}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4}>
                            <KeyboardDatePicker
                                label={t(`Note.Reference date`)}
                                name="reference_date"
                                value={note.reference_date}
                                disabled
                                format="dd/MM/yyyy"
                                variant="inline"
                                fullWidth
                                autoOk
                            />
                        </Grid>
                        {note.geom && showMap && <>
                            <Grid item xs={12} sm={8} md={8} className={classes.mapNoteContainer}>
                                <NoteMap parcel={parcel} marker={note?.geometry ? note.geometry : note.geom} params={note.parameters}
                                    selectedResolution={selectedResolution} />
                            </Grid>

                            <Grid item xs={12} sm={4} md={4}>
                                <div style={{ height: 400, overflow: 'auto' }}>
                                    {note.geometry && showMap && note.geometry.map(geometry =>
                                        <GeomNote key={geometry.id} geometry={geometry} disabled={true}/>
                                    )}
                                </div>
                            </Grid>
                        </>}
                    </Grid>

                    {note.attachments.map(att => <Link target="_blank" rel="noopener" href={att.attachment}>
                        <Chip
                            label={att.attachment.split("/").slice(-1)[0].split("?")[0]}
                            variant="outlined"
                            className={classes.attachment}
                            icon={<DescriptionIcon />} />
                    </Link>)}
                    {renderMenu}
                </CardContent>
                {note.geom &&
                    <CardActions disableSpacing>
                        <Button component="span"
                            onClick={handleShowMap}
                            startIcon={<RoomIcon />}
                            disableElevation
                            variant="outlined"
                            color="primary"
                            style={{ marginLeft: 'auto' }}>
                            {showMap ? t('Note.Close map') : t('Note.Show map')}
                        </Button>
                    </CardActions>}
            </Card>}
        </>
    );
}